import { createStorage } from '@sg/mmkv-storage';

// ─────────────────────────────────────────────────────────────────────────────

export function getActiveGroupOrderId() {
  const reference = getGroupOrderReference();

  return reference?.status === ACTIVE_STATUS ? reference.orderId : undefined;
}

export function checkIfActiveGroupOrderHost(orderId: string) {
  const reference = getGroupOrderReference();
  const isActiveOrder = reference?.orderId === orderId && reference.status === ACTIVE_STATUS;

  return isActiveOrder && reference.role === HOST_ROLE;
}

export function storeActiveGroupOrder(params: StoreActiveGroupOrderParams) {
  const { orderId, isHost } = params;

  const role = isHost ? HOST_ROLE : INVITEE_ROLE;
  const reference = getGroupOrderReference();
  const hasTrackedOrder =
    reference?.orderId === orderId && reference.role === role && reference.status === ACTIVE_STATUS;

  if (hasTrackedOrder) return;

  storeGroupOrderReference({ orderId, role, status: ACTIVE_STATUS });
}

export function checkIfGroupOrderDismissed(orderId: string) {
  const reference = getGroupOrderReference();

  return reference?.orderId === orderId && reference.status === DISMISSED_STATUS;
}

export function storeDismissedGroupOrderId(orderId: string) {
  const reference = getGroupOrderReference();
  const role = reference?.orderId === orderId ? reference.role : INVITEE_ROLE;

  storeGroupOrderReference({ orderId, role, status: DISMISSED_STATUS });
}

export function clearGroupOrderReference() {
  GROUP_ORDER_STORAGE.remove(GROUP_ORDER_REFERENCE_KEY);
}

/**
 * Whether the ended message can be shown for this order without replacing
 * another tracked group order or reopening one that was already dismissed.
 */
export function checkIfGroupOrderEndedMessageAllowed(orderId: string) {
  const reference = getGroupOrderReference();

  if (!reference) return true;

  const isTrackedOrder = reference.orderId === orderId && reference.status === ACTIVE_STATUS;

  return isTrackedOrder && reference.role === INVITEE_ROLE;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getGroupOrderReference() {
  const storedReference = GROUP_ORDER_STORAGE.getString(GROUP_ORDER_REFERENCE_KEY);

  if (!storedReference) return;

  try {
    const reference: unknown = JSON.parse(storedReference);

    return checkIfGroupOrderReference(reference) ? reference : undefined;
  } catch {
    return;
  }
}

function storeGroupOrderReference(reference: GroupOrderReference) {
  GROUP_ORDER_STORAGE.set(GROUP_ORDER_REFERENCE_KEY, JSON.stringify(reference));
}

function checkIfGroupOrderReference(reference: unknown): reference is GroupOrderReference {
  if (!reference || typeof reference !== 'object') return false;

  const referenceRecord = reference as Record<string, unknown>;
  const isKnownStatus =
    referenceRecord.status === ACTIVE_STATUS || referenceRecord.status === DISMISSED_STATUS;
  const isKnownRole = referenceRecord.role === HOST_ROLE || referenceRecord.role === INVITEE_ROLE;

  return typeof referenceRecord.orderId === 'string' && isKnownStatus && isKnownRole;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ORDER_STORAGE = createStorage({ id: 'sg.group-order' });
const GROUP_ORDER_REFERENCE_KEY = 'group-order-reference';
const ACTIVE_STATUS = 'active';
const DISMISSED_STATUS = 'dismissed';
const HOST_ROLE = 'host';
const INVITEE_ROLE = 'invitee';

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupOrderReference = {
  orderId: string;
  role: typeof HOST_ROLE | typeof INVITEE_ROLE;
  status: typeof ACTIVE_STATUS | typeof DISMISSED_STATUS;
};

type StoreActiveGroupOrderParams = {
  orderId: string;
  isHost: boolean;
};
